import { Controller } from "@hotwired/stimulus"
// Headless core only (KTD3): the package's pre-built UI is React — never
// import "@solana/connector/react". The custom UI below follows brand.md.
import { ConnectorClient, getDefaultConfig, ready } from "@solana/connector/headless"
import { workStatus, workError, setBusy } from "./work_status"

// Plain-language copy (R16). Raw wallet errors go to the console for
// debugging, never to the page.
export const COPY = {
  noWallets: "We couldn't find a wallet on this device. Install one, then come back and try again.",
  noWalletsOnPhone: "On a phone, the sure way in is to open sproot.money inside your wallet app's own browser " +
    "(in Phantom, the globe tab at the bottom).",
  phoneHint: "Not opening? Open sproot.money inside your wallet app's own browser instead " +
    "(in Phantom, the globe tab at the bottom).",
  mobileBlocked: "Your browser is blocking the connection to your wallet app. In this site's settings, allow it to connect to your device, then try again.",
  mobileAsking: "Your browser will ask whether Sproot may reach your wallet app. Tap Allow.",
  mobileConnected: "Wallet connected. One more tap opens your wallet app to confirm.",
  mobileConfirmButton: "Confirm in your wallet app",
  mobileNoWallet: "We couldn't find a wallet app on this phone. Install Phantom or Solflare, then come back and try again.",
  connectFailed: "We couldn't connect to that wallet. Please try again.",
  confirmFailed: "That confirmation didn't go through. Please try again.",
  wrongWallet: "That's a different wallet from the one your plan uses. Connect the one your plan was set up with.",
  rejected: "That didn't go through. You can try again.",
  network: "Something went wrong on our side. Please try again in a moment."
}

// The wallet-picking half of every page that talks to a wallet: open the
// registry, list the wallets this device actually has, and hand the chosen one
// to the page. What happens after the wallet is chosen is the subclass's job.
export default class WalletConnectorController extends Controller {
  static targets = [ "list", "empty", "error", "status" ]
  static values = {
    appName: { type: String, default: "Sproot" },
    cluster: { type: String, default: "devnet" }
  }

  async connect() {
    await registerMobileWallet(this.appNameValue, this.clusterValue)
    this.client = new ConnectorClient(
      getDefaultConfig({ appName: this.appNameValue, network: this.clusterValue, autoConnect: false })
    )
    this.unsubscribe = this.client.subscribe(() => this.renderWallets())
    try {
      await ready
    } catch (error) {
      this.log("registry failed", error)
    }
    this.renderWallets()
  }

  disconnect() {
    if (this.unsubscribe) this.unsubscribe()
    if (this.client) this.client.destroy()
  }

  renderWallets() {
    if (this.connected) return

    const { connectors } = this.client.getSnapshot()
    const usable = connectors.filter((connector) => connector.ready)

    if (usable.length === 0) {
      this.emptyTarget.textContent = onPhone() ? COPY.noWalletsOnPhone : COPY.noWallets
      this.emptyTarget.hidden = false
      return
    }

    this.emptyTarget.hidden = true
    this.listTarget.querySelectorAll("button, [data-phone-hint]").forEach((node) => node.remove())
    usable.forEach((connector) => {
      const button = document.createElement("button")
      button.type = "button"
      button.dataset.connectorId = connector.id
      button.className =
        "w-full rounded-[10px] bg-surface border-[1.5px] border-paper-rule font-semibold px-5 py-3.5 " +
        "text-left inline-flex items-center gap-3 cursor-pointer transition-colors " +
        "hover:border-field hover:text-field"
      const icon = document.createElement("img")
      icon.src = connector.icon
      icon.alt = ""
      icon.className = "w-6 h-6 rounded"
      const label = document.createElement("span")
      label.textContent = displayName(connector)
      button.append(icon, label)
      button.addEventListener("click", () => {
        // Chrome raises its local-network prompt only for a request made in
        // the same instant as the tap: any await before it and the prompt
        // stays away (2026-09-14). So the request goes out here, and
        // ensureLoopbackPermission only waits for the answer.
        if (isMobileAdapter(connector)) primeLoopbackPrompt()
        this.selectWallet(connector.id)
      })
      this.listTarget.append(button)
    })

    // The hand-off to a wallet app depends on the phone's browser raising a
    // permission prompt, and Chrome on Android (153, 2026-09) does not; the
    // wallet app's own browser injects the wallet directly and always works.
    if (onPhone()) {
      const hint = document.createElement("p")
      hint.dataset.phoneHint = ""
      hint.className = "text-sm leading-relaxed text-paper-muted"
      hint.textContent = COPY.phoneHint
      this.listTarget.append(hint)
    }
  }

  // What clicking a wallet in the list does. Subclasses override.
  selectWallet(_connectorId) {}

  // Chrome on Android asks, once per site, before a page may reach the wallet
  // app on the phone. The adapter tries to raise that prompt from inside its
  // own dialog and Chrome 153 does not answer it (2026-09-14, Pixel 8a); a
  // request made straight from the investor's tap on our page does raise it.
  // So the prompt is raised here, and the adapter
  // then finds the permission already granted and goes straight to the app.
  async ensureLoopbackPermission() {
    let permission
    try {
      permission = await navigator.permissions.query({ name: "loopback-network" })
    } catch {
      return // a browser without this permission has nothing to ask
    }
    if (permission.state === "granted") return
    if (permission.state === "denied") throw loopbackBlocked("denied")

    if (this.hasStatusTarget) this.status(COPY.mobileAsking, "waiting")
    const answered = new Promise((resolve) => {
      permission.onchange = () => { permission.onchange = null; resolve(permission.state) }
      setTimeout(() => resolve(permission.state), 60_000)
    })
    primeLoopbackPrompt()
    const state = await answered
    if (state !== "granted") throw loopbackBlocked(state)
  }

  // Opens the wallet and hands back the account it selected. A wallet the
  // investor closed instead of approving throws `rejected` (a softer message);
  // anything else throws with the underlying reason attached for the console.
  async openWallet(connectorId) {
    try {
      if (isMobileAdapter(this.client.getConnector(connectorId))) await this.ensureLoopbackPermission()
      await this.client.connectWallet(connectorId)
    } catch (error) {
      if (isUserRejection(error)) {
        const rejected = new Error("wallet connect dismissed")
        rejected.rejected = true
        throw rejected
      }
      const explained = mobileWalletMessage(error)
      if (explained) error.userMessage = explained
      throw error
    }
    const snapshot = this.client.getSnapshot()
    const wallet = snapshot.wallet
    if (wallet.status === "connecting") {
      // The extension is still asking the user; treat a click that hasn't
      // resolved as a dismissal rather than a hard failure.
      const rejected = new Error("wallet still connecting")
      rejected.rejected = true
      throw rejected
    }
    if (wallet.status !== "connected" || !wallet.session?.selectedAccount) {
      throw new Error(`wallet reported "${wallet.status}", not connected`)
    }
    return (await this.activeAccount(connectorId, wallet.session)) ?? wallet.session.selectedAccount
  }

  // After its user switches accounts, Phantom still lists the site's earlier
  // account next to the active one, the connector selects the first, and the
  // wallet then signs with the active key: the server refuses a signature
  // from the wrong account (production, 2026-11-02). With more than one
  // account on offer, ask the wallet silently which one it is on.
  async activeAccount(connectorId, session) {
    if (session.accounts.length < 2) return null
    const connector = this.client.getConnector(connectorId)
    const connect = connector?.features?.["standard:connect"]
    if (!connect || isMobileAdapter(connector)) return null
    try {
      const { accounts } = await connect.connect({ silent: true })
      const active = accounts?.[0]
      if (!active) return null
      return session.accounts.find((a) => a.address === active.address) ??
        { address: active.address, label: active.label, account: active }
    } catch (error) {
      this.log("active account check failed", error)
      return null
    }
  }

  async postJSON(url, body) {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      body: JSON.stringify(body)
    })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok) {
      const error = new Error(`request failed: ${response.status}`)
      error.userMessage = payload.error || COPY.network
      // The server may name the record's new state alongside the refusal — a
      // leg it decided to skip, say. Callers that care read it off the error.
      error.serverStatus = payload.status
      throw error
    }
    return payload
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }

  // Where the status line lands when connecting or signing falls through.
  get idleStatus() {
    return "Choose a wallet to continue"
  }

  // The status line, with its mark and (optionally) the flow's step line —
  // lib/work_status.js. state: idle | working | waiting | done | error.
  status(text, state = "working", steps = {}) {
    workStatus(this.statusTarget, text, state, steps)
  }

  fail(copy) {
    this.status(this.idleStatus, "idle")
    workError(this.errorTarget, copy)
    if (this.hasListTarget) this.listTarget.querySelectorAll("button[data-busy]").forEach((button) => setBusy(button, false))
  }

  clearError() {
    this.errorTarget.hidden = true
    this.errorTarget.textContent = ""
  }

  log(message, error) {
    console.debug(`[${this.identifier}] ${message}`, error)
  }
}

// On a phone there is no extension to inject a wallet: the Mobile Wallet
// Adapter registers one that hands the request to an installed wallet app
// (Phantom, Solflare) and comes back with the answer. The register call is
// a no-op where the platform cannot do that (desktop, iOS), so the list
// only ever shows what the device can actually use.
let mobileWalletRegistered = false
async function registerMobileWallet(appName, cluster) {
  if (mobileWalletRegistered || typeof window === "undefined") return
  mobileWalletRegistered = true
  try {
    const {
      registerMwa, createDefaultAuthorizationCache, createDefaultChainSelector, createDefaultWalletNotFoundHandler
    } = await import("@solana-mobile/wallet-standard-mobile")
    registerMwa({
      appIdentity: { name: appName, uri: window.location.origin, icon: "/icon.png" },
      authorizationCache: droppingWhatPhantomIgnores(createDefaultAuthorizationCache()),
      chains: [ `solana:${cluster}` ],
      chainSelector: createDefaultChainSelector(),
      onWalletNotFound: createDefaultWalletNotFoundHandler()
    })
  } catch (error) {
    console.debug("[wallet] mobile wallet adapter unavailable", error)
  }
}

// Two things the wallet hands back on the first connection, both dropped as
// they arrive, because Phantom on Android honours neither on the trips that
// follow. The object is shared with the adapter's own state, so removing a
// field here is what changes the next trip.
//
// The address for later trips (`wallet_uri_base`): Phantom opens it as an
// ordinary link and lands on its home screen, while the generic wallet link
// the first trip uses reaches its request handler every time (2026-09-14,
// Pixel 8a).
//
// The token that stands in for the connection (`auth_token`): the adapter
// spends it on `reauthorize` at the top of every later trip, and Phantom
// answers its own token with `{"code":-1,"message":"authorization request
// failed"}` in a quarter of a second — it opens with nothing to show, having
// declined before there was anything to ask (2026-09-18, Pixel 8a, Chrome
// 153, USB console). Worse, the adapter treats that refusal as a dropped
// connection and then awaits a promise it never settles, so the signature
// never returns and never fails: the page waits on the wallet forever.
// Without a token the adapter authorizes afresh instead, which Phantom
// answers with its sign-in sheet, and the signing request follows in the same
// session — one trip, the way the first one works.
function droppingWhatPhantomIgnores(cache) {
  const strip = (authorization) => {
    if (authorization) {
      delete authorization.wallet_uri_base
      delete authorization.auth_token
    }
    return authorization
  }
  return {
    get: async () => strip(await cache.get()),
    set: (authorization) => cache.set(strip(authorization)),
    clear: () => cache.clear()
  }
}

// A wallet request the adapter will never answer, either way. Asking a wallet
// to sign goes through the adapter's reauthorize step, and when that step
// fails — the investor closing the wallet's sheet is enough — the adapter
// drops the connection and then waits on a promise it never settles
// (2026-09-18: `#transact` answers a changed connection generation with
// `await new Promise(() => {})`). Nothing rejects, so no catch of ours can
// run and the page would sit on "check your wallet" until it is reloaded.
// A request still unanswered this long is treated as one the investor walked
// away from, which is the likeliest way to arrive here and leaves the page
// saying something they can act on.
const WALLET_ANSWER_LIMIT = 3 * 60_000

export async function withinWalletAnswerLimit(request) {
  let timer
  const gaveUp = new Promise((_, reject) => {
    timer = setTimeout(() => {
      const error = new Error("wallet never answered")
      error.timedOut = true
      error.userMessage = COPY.confirmFailed
      reject(error)
    }, WALLET_ANSWER_LIMIT)
  })
  try {
    return await Promise.race([ request, gaveUp ])
  } finally {
    clearTimeout(timer)
  }
}

// A phone, as opposed to a laptop with a touchscreen: no extension can be
// installed, so the wallet has to come from an app.
function onPhone() {
  if (navigator.userAgentData?.mobile !== undefined) return navigator.userAgentData.mobile
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
}

export function isMobileAdapter(connector) {
  return /mobile wallet adapter/i.test(connector?.name || "")
}

// The request itself always fails at once; Chrome's permission prompt is the
// point. Sent at most once per tap, so the flow's own call is a no-op when
// the click handler already sent it.
let loopbackPrimedAt = 0
function primeLoopbackPrompt() {
  if (Date.now() - loopbackPrimedAt < 2_000) return
  loopbackPrimedAt = Date.now()
  try { fetch("http://localhost").catch(() => {}) } catch {}
}

function loopbackBlocked(state) {
  const error = new Error(`loopback permission ${state}`)
  error.code = "ERROR_LOOPBACK_ACCESS_BLOCKED"
  return error
}

// The mobile adapter registers under its protocol's name; on the page it is
// the wallet app on this phone, whichever one the investor has installed.
function displayName(connector) {
  return isMobileAdapter(connector) ? "Wallet app on this phone" : connector.name
}

// The mobile adapter's own failure codes, in plain words where the investor
// can do something about them. Chrome on Android asks permission before a
// page may talk to the wallet app; a refused or unanswered prompt is the
// common one.
function mobileWalletMessage(error) {
  switch (error?.code) {
    case "ERROR_LOOPBACK_ACCESS_BLOCKED": return COPY.mobileBlocked
    case "ERROR_WALLET_NOT_FOUND": return COPY.mobileNoWallet
    case "ERROR_SESSION_TIMEOUT": return COPY.rejected
    default: return null
  }
}

// A wallet the user closed/declined vs. a genuine failure. Wallet-standard and
// the common extensions surface rejection through an error name or a code
// (4001 is the de-facto "user rejected") or plain "reject/denied/cancel" text.
export function isUserRejection(error) {
  if (!error) return false
  if (error.code === 4001 || error.name === "WalletConnectionError") return true
  return /reject|declin|denied|cancel|dismiss|closed/i.test(String(error.message || error))
}
